"use client";

import type React from "react";
import { useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Spinner,
  useDisclosure,
  useToast,
  IconButton,
  HStack,
  List,
  ListItem,
} from "@chakra-ui/react";
import { AddIcon, RepeatIcon, DeleteIcon } from "@chakra-ui/icons";
import { FileText, Book, Copy } from "lucide-react";
import Layout from "./Layout.tsx";
import {
  knowledgeBaseService,
  type KnowledgeBase as KnowledgeBaseType,
} from "../services/knowledgeBaseService.ts";
import { useAppTheme } from "../ThemeContext.tsx";
import { AddKnowledgeBaseModal } from "./knowledge-base/AddKnowledgeBaseModal.tsx";
import { KnowledgeBaseDetails } from "./knowledge-base/KnowledgeBaseDetails.tsx";
import { useState } from "react";

function getShortId(id) {
  if (!id) return "";
  const len = id.length;
  return len > 20 ? `${id.slice(0, 15)}...${id.slice(-3)}` : id;
}

const KnowledgeBase: React.FC = () => {
  const [knowledgeBases, setKnowledgeBases] = useState<
    KnowledgeBaseType[] | null
  >(null);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] =
    useState<KnowledgeBaseType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const loadKnowledgeBases = useCallback(async () => {
    try {
      const data = await knowledgeBaseService.list();
      setKnowledgeBases(data);
      if (data.length > 0 && !selectedKnowledgeBase) {
        setSelectedKnowledgeBase(data[0]);
      }
    } catch (error: any) {
      console.error("Error loading knowledge bases:", error);
      toast({
        title: "Error loading knowledge bases",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
      setKnowledgeBases([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedKnowledgeBase, toast]);

  useEffect(() => {
    loadKnowledgeBases();
  }, [loadKnowledgeBases]);

  const hasWebSources = (kb: KnowledgeBaseType) => {
    return (
      kb.knowledge_base_sources?.some((source) => source.type === "url") ??
      false
    );
  };

  const handleAddSuccess = () => {
    loadKnowledgeBases();
    onAddClose();
  };

  const handleRefresh = async () => {
    if (selectedKnowledgeBase && hasWebSources(selectedKnowledgeBase)) {
      try {
        // Get the current knowledge base details
        const currentKB = await knowledgeBaseService.get(
          selectedKnowledgeBase.knowledge_base_id
        );

        // Filter out URL sources
        const urlSources = currentKB.knowledge_base_sources.filter(
          (source) => source.type === "url"
        );

        // Extract URLs
        const urls = urlSources
          .map((source) => source.url)
          .filter((url): url is string => url !== undefined);

        if (urls.length > 0) {
          // Add new URL sources
          await knowledgeBaseService.addUrls(currentKB.knowledge_base_id, urls);

          // Delete old URL sources
          for (const source of urlSources) {
            await knowledgeBaseService.removeSource(
              currentKB.knowledge_base_id,
              source.source_id
            );
          }

          // Fetch the updated knowledge base
          const updated = await knowledgeBaseService.get(
            currentKB.knowledge_base_id
          );

          setKnowledgeBases((prev) =>
            prev?.map((kb) =>
              kb.knowledge_base_id === updated.knowledge_base_id ? updated : kb
            )
          );
          setSelectedKnowledgeBase(updated);
          setIsPolling(true);

          toast({
            title: "Knowledge base re-synced",
            description:
              "URL sources are now uploading. They will be ready to use shortly.",
            status: "success",
            duration: 3000,
          });
        } else {
          toast({
            title: "No URL sources to re-sync",
            description: "This knowledge base doesn't have any URL sources",
            status: "info",
            duration: 3000,
          });
        }
      } catch (error: any) {
        console.error("Error re-syncing knowledge base:", error);
        toast({
          title: "Error re-syncing knowledge base",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 5000,
        });
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDelete = async (knowledgeBaseId: string) => {
    try {
      await knowledgeBaseService.delete(knowledgeBaseId);
      toast({
        title: "Knowledge base deleted",
        status: "success",
        duration: 3000,
      });
      setSelectedKnowledgeBase(null);
      loadKnowledgeBases();
    } catch (error: any) {
      toast({
        title: "Error deleting knowledge base",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <Flex justify="center" align="center" h="100vh" w="100%">
          <Spinner size="xl" />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex h="100vh">
        {/* Sidebar */}
        <Box
          w="300px"
          bg={isDark ? "gray.900" : "gray.50"}
          borderRight="1px"
          borderColor={isDark ? "gray.700" : "gray.200"}
        >
          <Flex
            p={4}
            justify="space-between"
            align="center"
            borderBottom="1px"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            <HStack spacing={3}>
              <Book size={20} />
              <Heading size="md" color={isDark ? "white" : "gray.800"}>
                Knowledge Base
              </Heading>
            </HStack>
            <IconButton
              aria-label="Add knowledge base"
              icon={<AddIcon />}
              size="sm"
              onClick={onAddOpen}
              colorScheme={isDark ? "gray" : "blackAlpha"}
            />
          </Flex>

          <List spacing={0}>
            {knowledgeBases?.map((kb) => (
              <ListItem
                key={kb.knowledge_base_id}
                p={4}
                cursor="pointer"
                bg={
                  selectedKnowledgeBase?.knowledge_base_id ===
                  kb.knowledge_base_id
                    ? isDark
                      ? "gray.700"
                      : "gray.300"
                    : "transparent"
                }
                _hover={{ bg: isDark ? "gray.500" : "gray.200" }}
                onClick={() => setSelectedKnowledgeBase(kb)}
              >
                <HStack spacing={3}>
                  <FileText size={16} />
                  <Text color={isDark ? "white" : "gray.800"}>
                    {kb.knowledge_base_name}
                  </Text>
                </HStack>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Main Content */}
        <Box flex="1" bg={isDark ? "gray.800" : "white"}>
          {selectedKnowledgeBase ? (
            <Box>
              <Flex
                justify="space-between"
                align="center"
                p={4}
                borderBottom="1px"
                borderColor={isDark ? "gray.700" : "gray.200"}
              >
                <VStack align="start" spacing={1}>
                  <Heading size="lg" color={isDark ? "white" : "gray.800"}>
                    {selectedKnowledgeBase.knowledge_base_name}
                  </Heading>
                  <Text fontSize="sm" color={isDark ? "gray.400" : "gray.600"}>
                    ID: {getShortId(selectedKnowledgeBase.knowledge_base_id)}
                    <IconButton
                      aria-label="Copy Knowledge Base ID"
                      icon={<Copy size={12} />}
                      size="xs"
                      variant="ghost"
                      onClick={() =>
                        copyToClipboard(selectedKnowledgeBase.knowledge_base_id)
                      }
                      color="gray.500"
                    />
                  </Text>
                </VStack>
                <HStack spacing={2}>
                  {selectedKnowledgeBase &&
                    hasWebSources(selectedKnowledgeBase) && (
                      <Button
                        leftIcon={<RepeatIcon />}
                        onClick={handleRefresh}
                        size="sm"
                        variant="outline"
                      >
                        Re-sync
                      </Button>
                    )}
                  <IconButton
                    aria-label="Delete knowledge base"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    color={isDark ? "red.300" : "red.500"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                    onClick={() =>
                      handleDelete(selectedKnowledgeBase.knowledge_base_id)
                    }
                  />
                </HStack>
              </Flex>
              <Box p={4}>
                <KnowledgeBaseDetails
                  knowledgeBase={selectedKnowledgeBase}
                  onRefresh={(updated) => {
                    if (updated) {
                      setSelectedKnowledgeBase(updated);
                      setKnowledgeBases((prev) =>
                        prev?.map((kb) =>
                          kb.knowledge_base_id === updated.knowledge_base_id
                            ? updated
                            : kb
                        )
                      );
                      if (
                        updated.status === "complete" ||
                        updated.status === "error"
                      ) {
                        setIsPolling(false);
                      }
                    }
                  }}
                  isPolling={isPolling}
                  setIsPolling={setIsPolling}
                />
              </Box>
            </Box>
          ) : (
            <Flex
              justify="center"
              align="center"
              h="100%"
              color={isDark ? "gray.400" : "gray.600"}
            >
              Select a knowledge base to view details
            </Flex>
          )}
        </Box>
      </Flex>

      <AddKnowledgeBaseModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSuccess={handleAddSuccess}
      />
    </Layout>
  );
};

export default KnowledgeBase;
